import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SettingSection from './components/SettingSection';
import SettingItem from './components/SettingItem';
import ThemeToggle from './components/ThemeToggle';
import FontSizeSlider from './components/FontSizeSlider';
import ConfirmationModal from './components/ConfirmationModal';
import ToastNotification from './components/ToastNotification';

const Settings = () => {
  const navigate = useNavigate();

  const [defaultSort, setDefaultSort] = useState('due-date');
  const [notifications, setNotifications] = useState({
    taskReminders: true,
    dailySummary: false,
    weeklyReport: true
  });
  const [colorContrast, setColorContrast] = useState('normal');
  const [keyboardNav, setKeyboardNav] = useState(true);
  
  const [profileName, setProfileName] = useState('John Doe');
  const [profileEmail, setProfileEmail] = useState('john.doe@example.com');
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const sortOptions = [
    { value: 'due-date', label: 'Due Date' },
    { value: 'created-date', label: 'Created Date' },
    { value: 'priority', label: 'Priority' },
    { value: 'custom', label: 'Custom Order' }
  ];

  const contrastOptions = [
    { value: 'normal', label: 'Normal' },
    { value: 'high', label: 'High Contrast' },
    { value: 'maximum', label: 'Maximum Contrast' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
  };

  const handleExportData = (format) => {
    const mockData = {
      tasks: [
        { id: 1, title: 'Complete project proposal', quadrant: 'urgent-important', dueDate: '2026-02-10' },
        { id: 2, title: 'Review team feedback', quadrant: 'not-urgent-important', dueDate: '2026-02-15' }
      ],
      settings: { defaultSort, notifications, colorContrast, keyboardNav },
      exportDate: new Date()?.toISOString()
    };

    const dataStr = format === 'json' 
      ? JSON.stringify(mockData, null, 2)
      : `Task ID,Title,Quadrant,Due Date\n${mockData?.tasks?.map(t => `${t?.id},${t?.title},${t?.quadrant},${t?.dueDate}`)?.join('\n')}`;

    const blob = new Blob([dataStr], { type: format === 'json' ? 'application/json' : 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `eisenhower-matrix-backup-${new Date()?.toISOString()?.split('T')?.[0]}.${format}`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);

    setShowExportModal(false);
    showToast(`Data exported successfully as ${format?.toUpperCase()}`, 'success');
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(false);
    showToast('Account deletion initiated. You will be logged out shortly.', 'info');
    setTimeout(() => {
      navigate('/');
    }, 2000);
  };

  const handleSaveProfile = () => {
    showToast('Profile updated successfully', 'success');
  };

  const handlePasswordChange = () => {
    showToast('Password change functionality will be available soon', 'info');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-8 px-4 md:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-2">
              Settings
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Customize your Eisenhower Matrix experience
            </p>
          </div>

          <SettingSection
            title="User Preferences"
            description="Configure default behaviors and sorting options"
            defaultExpanded={true}
          >
            <SettingItem
              label="Default Task Sorting"
              description="Choose how tasks are sorted by default in each quadrant"
            >
              <Select
                options={sortOptions}
                value={defaultSort}
                onChange={setDefaultSort}
                className="w-full sm:w-48"
              />
            </SettingItem>

            <SettingItem
              label="Theme"
              description="Switch between light and dark mode"
            >
              <ThemeToggle />
            </SettingItem>

            <div className="border-t border-border my-4" />

            <div className="space-y-3">
              <h4 className="text-sm md:text-base font-medium text-foreground mb-3">
                Notification Preferences
              </h4>
              <Checkbox
                label="Task Reminders"
                description="Get notified about upcoming task deadlines"
                checked={notifications?.taskReminders}
                onChange={(e) => setNotifications({ ...notifications, taskReminders: e?.target?.checked })}
              />
              <Checkbox
                label="Daily Summary"
                description="Receive a daily summary of your tasks"
                checked={notifications?.dailySummary}
                onChange={(e) => setNotifications({ ...notifications, dailySummary: e?.target?.checked })}
              />
              <Checkbox
                label="Weekly Report"
                description="Get a weekly productivity report"
                checked={notifications?.weeklyReport}
                onChange={(e) => setNotifications({ ...notifications, weeklyReport: e?.target?.checked })}
              />
            </div>
          </SettingSection>

          <SettingSection
            title="Accessibility Options"
            description="Adjust settings for better accessibility"
          >
            <SettingItem
              label="Color Contrast"
              description="Adjust color contrast for better visibility"
            >
              <Select
                options={contrastOptions}
                value={colorContrast}
                onChange={setColorContrast}
                className="w-full sm:w-48"
              />
            </SettingItem>

            <SettingItem
              label="Font Size"
              description="Adjust the base font size for all text"
            >
              <FontSizeSlider />
            </SettingItem>

            <SettingItem
              label="Keyboard Navigation"
              description="Enable enhanced keyboard shortcuts"
            >
              <Checkbox
                checked={keyboardNav}
                onChange={(e) => setKeyboardNav(e?.target?.checked)}
              />
            </SettingItem>
          </SettingSection>

          <SettingSection
            title="Data Management"
            description="Export, import, and manage your task data"
          >
            <SettingItem
              label="Export Data"
              description="Download your tasks and settings as a backup"
            >
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={() => setShowExportModal(true)}
              >
                Export
              </Button>
            </SettingItem>

            <SettingItem
              label="Import Data"
              description="Restore tasks from a previous backup file"
            >
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left"
                onClick={() => showToast('Import functionality will be available soon', 'info')}
              >
                Import
              </Button>
            </SettingItem>
          </SettingSection>

          <SettingSection
            title="Account Management"
            description="Manage your profile and account settings"
          >
            <div className="space-y-4">
              <Input
                label="Full Name"
                type="text"
                value={profileName}
                onChange={(e) => setProfileName(e?.target?.value)}
              />
              <Input
                label="Email Address"
                type="email"
                value={profileEmail}
                onChange={(e) => setProfileEmail(e?.target?.value)}
              />
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button
                  variant="default"
                  onClick={handleSaveProfile}
                  className="w-full sm:w-auto"
                >
                  Save Profile
                </Button>
                <Button
                  variant="outline"
                  onClick={handlePasswordChange}
                  className="w-full sm:w-auto"
                >
                  Change Password
                </Button>
              </div>
            </div>

            <div className="border-t border-border my-6" />

            <SettingItem
              label="Delete Account"
              description="Permanently delete your account and all associated data"
            >
              <Button
                variant="destructive"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </Button>
            </SettingItem>
          </SettingSection>
        </div>
      </main>
      <ConfirmationModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onConfirm={() => handleExportData('json')}
        title="Export Data"
        message="Choose the format for your data export. JSON format includes all settings and metadata, while CSV is optimized for spreadsheet applications."
        type="info"
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Account"
        message="Are you sure you want to delete your account? This action cannot be undone and all your tasks and data will be permanently removed."
        type="danger"
      />
      <ToastNotification
        message={toast?.message}
        type={toast?.type}
        isVisible={toast?.visible}
        onClose={() => setToast({ ...toast, visible: false })}
      />
    </div>
  );
};

export default Settings;